import { useLiveQuery } from '@tanstack/react-db'

import { useDb } from '@/lib/dbContext'
import { fromUtcIsoToZaParts } from '@/lib/datetime'
import type { Callout } from '@/lib/schemas/db'

export interface DayBookingsPreviewProps {
  /** ZA-local date key (YYYY-MM-DD) of the day being booked. */
  day: string
  /** Proposed window (HH:mm). Existing bookings overlapping it are flagged. */
  time?: string
  endTime?: string
}

interface DayRow {
  id: string
  windowLabel: string
  customerName: string
  kindLabel: string
  overlaps: boolean
  sortKey: string
}

function serviceKindLabel(booking: Callout): string {
  if (booking.sessionSubtype === 'TeamViewer Session') return 'TeamViewer'
  if (booking.sessionSubtype === 'Sit-down Session') return 'Sit-down'
  return 'Callout'
}

function toDayRow(
  booking: Callout,
  day: string,
  time?: string,
  endTime?: string,
): DayRow | null {
  if (booking.status === 'Cancelled') return null
  const start = fromUtcIsoToZaParts(booking.startTime)
  if (start.day !== day) return null
  const end = fromUtcIsoToZaParts(booking.endTime)

  // Zero-padded HH:mm compares correctly as strings. A whole-day booking
  // overlaps any proposed window on its day.
  const overlaps = Boolean(
    time &&
      endTime &&
      (booking.isWholeDay || (start.time < endTime && time < end.time)),
  )

  return {
    id: booking.id,
    windowLabel: booking.isWholeDay
      ? 'All day'
      : `${start.time}–${end.time}`,
    customerName: booking.contactName || 'Unnamed customer',
    kindLabel: serviceKindLabel(booking),
    overlaps,
    sortKey: booking.isWholeDay ? '' : start.time,
  }
}

/**
 * What's already booked on the day being picked, shown inside the booking
 * modal so clashes are visible *while* choosing, not after. Rows overlapping
 * the proposed time window are flagged; an empty day says so, so silence
 * reads as "clear" instead of "didn't load".
 */
export function DayBookingsPreview({ day, time, endTime }: DayBookingsPreviewProps) {
  const db = useDb()
  const calloutsResult = useLiveQuery((q) =>
    q.from({ callouts: db.calloutsCollection }),
  )
  const sessionsResult = useLiveQuery((q) =>
    q.from({ sessions: db.sessionsCollection }),
  )

  if (!day || calloutsResult.isLoading || sessionsResult.isLoading) return null

  const rows = [
    ...(calloutsResult.data ?? []),
    ...(sessionsResult.data ?? []),
  ]
    .map((booking) => toDayRow(booking as Callout, day, time, endTime))
    .filter((row): row is DayRow => row !== null)
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2.5">
      <p className="mb-1.5 text-[10px] font-semibold tracking-wider text-slate-500 uppercase">
        Already booked this day
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-500">
          Nothing else is booked on this day.
        </p>
      ) : (
        <ul className="space-y-1">
          {rows.map((row) => (
            <li
              key={row.id}
              className={`flex flex-wrap items-baseline gap-x-2 text-[11.5px] ${
                row.overlaps ? 'text-rose-700' : 'text-slate-600'
              }`}
            >
              <span className="font-mono text-[11px]">{row.windowLabel}</span>
              <span className="font-medium">{row.customerName}</span>
              <span className={row.overlaps ? 'text-rose-500' : 'text-slate-400'}>
                {row.kindLabel}
              </span>
              {row.overlaps ? (
                <span className="rounded-full border border-rose-200 bg-rose-50 px-1.5 text-[10px] font-semibold">
                  overlaps
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
